<template>
  <div class="height100">
    <TopMenu :leftOption="'back-dashboard'" :title="$t('Jingles')">
      <template slot="right">
        <a href="#" @click.prevent="showUploadForm = !showUploadForm">
          <FontAwesomeIcon icon="plus" />
        </a>
      </template>
    </TopMenu>
    <div class="view height100">
      <transition name="fade" appear>
        <div class="height100">
          <!-- Upload Form -->
          <transition name="fade">
            <div v-if="showUploadForm" class="p-4 bg-dark mb-2 mx-1 rounded">
              <div class="mb-3">
                <small class="text-muted">
                  {{ $t('File requirements') }}:
                  {{ $t('MP3 format, 128kbps CBR stereo') }},
                  {{ $t('44100 Hz sample rate') }},
                  {{ $t('Maximum 60 seconds') }},
                  {{ $t('Maximum 1 MB') }}
                </small>
              </div>

              <input
                v-model="uploadTitle"
                type="text"
                :placeholder="$t('Jingle title')"
                class="form-control rounded border-primary mb-3"
              >

              <input
                type="file"
                ref="fileInput"
                accept="audio/mp3,audio/mpeg,.mp3"
                @change="onFileSelected"
                style="display: none"
              >
              <div class="d-flex align-items-center mb-3">
                <button
                  class="btn btn-outline-primary"
                  @click="$refs.fileInput.click()"
                >
                  {{ $t('Select file') }}
                </button>
                <span class="ml-3 text-white" v-if="selectedFile">
                  {{ selectedFile.name | truncate(20) }}
                </span>
                <span class="ml-3 text-muted" v-else>
                  {{ $t('No file selected') }}
                </span>
              </div>

              <button
                class="btn btn-primary w-100"
                :disabled="!canUpload || uploading"
                @click="uploadJingle"
              >
                <FontAwesomeIcon v-if="uploading" icon="spinner" spin class="mr-2" />
                {{ $t('Upload') }}
              </button>
            </div>
          </transition>

          <!-- Order Custom Jingle toggle button -->
          <div class="px-4 mb-2">
            <button
              class="btn btn-outline-secondary w-100"
              @click="showOrderForm = !showOrderForm"
            >
              {{ $t('Order Custom Jingle') }}
              <FontAwesomeIcon :icon="showOrderForm ? 'chevron-up' : 'chevron-down'" class="ml-2" />
            </button>
          </div>

          <!-- Order form -->
          <transition name="fade">
            <div v-if="showOrderForm" class="p-4 bg-dark mb-2 mx-1 rounded">
              <p class="text-muted mb-3">
                {{ $t('Order jingle description') }}
              </p>
              <textarea
                v-model="orderText"
                rows="4"
                class="form-control rounded border-primary mb-3"
                :placeholder="$t('Order jingle description')"
              ></textarea>
              <button
                class="btn btn-primary w-100"
                :disabled="!orderText.trim() || ordering"
                @click="orderJingle"
              >
                <FontAwesomeIcon v-if="ordering" icon="spinner" spin class="mr-2" />
                {{ $t('Order') }}
              </button>
            </div>
          </transition>

          <div class="p-4">
            <div class="input-group rounded">
              <div class="input-group-prepend">
                <span class="input-group-text rounded-left border-right-0 border-primary" id="addon">
                  <FontAwesomeIcon icon="search" />
                </span>
              </div>
              <input v-model="search" type="text" :placeholder="$t('Search')" class="form-control rounded-right border-left-0 border-primary">
            </div>
          </div>
          <Loader :display="loadingJingles" />
          <div class="height100_scrollable_list" v-on:scroll="scrollHandler($event)">
            <div v-for="jingle in jinglesForDisplay" :key="jingle.id" class="px-1 mb-1">
              <div class="bg-dark px-4 py-1 d-block d-flex justify-content-between align-items-center rounded clickable">
                <span
                  class="py-2 pr-2 mr-2 text-muted font-button"
                  :style="{ minWidth: (isPlaying || jingle.preview_url == null || jingle.preview_url === '') ? '0' : '30px' }"
                  @click="previewClick(jingle)"
                >
                  <span :style="{ display: !isPlaying && previewJingleId === null ? 'block' : 'none' }">
                    <FontAwesomeIcon icon="play" class="text-primary" />
                  </span>
                  <span :style="{ display: !isPlaying && previewJingleId !== null && jingle.id === previewJingleId ? 'block' : 'none' }">
                    <FontAwesomeIcon icon="pause" class="text-primary" />
                  </span>
                </span>
                <div style="flex: 1">
                  <p class="text-white m-0 p-0">{{ jingle.title }} <br>
                    <small class="text-muted">{{ jingle.artist }} - {{ jingle.minutesAndSeconds }}</small>
                  </p>
                </div>
                <span
                  class="py-2 pl-2 text-danger font-button"
                  @click.stop="confirmDeleteJingle(jingle)"
                >
                  <FontAwesomeIcon icon="trash-alt" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Delete confirmation modal -->
    <Modal
      :displayModal="jingleToDelete !== null"
      :onClose="() => { jingleToDelete = null }"
      :onConfirm="deleteJingle"
      :body="$t('Delete jingle') + ' ?'"
    ></Modal>
    <OverlayLoader :displayOverlay="deleting" />
  </div>
</template>

<script>
import musicboxClient from '@/utils/musicboxClient'
import { secondsToMinAndSec } from '@/utils/numberUtils'
import scrollHandler from '@/utils/scrollHandler'

export default {

  name: 'Jingles',

  beforeDestroy () {
    if (this.preview != null) {
      this.preview.pause()
      this.preview = null
    }
  },

  created () {
    this.loadingJingles = true
    musicboxClient
      .getJingles()
      .then(res => {
        res.data.forEach(jingle => {
          jingle.minutesAndSeconds = secondsToMinAndSec(jingle.duration)
          jingle.titleLc = jingle.title.toLowerCase()
        })
        this.jingles = res.data
      })
      .catch(e => { this.$notify.danger(this.$t('Failed to get jingles')) })
      .then(() => { this.loadingJingles = false })
  },

  data () {
    return {
      jingles: [],
      displayLimit: 30,
      loadingJingles: false,
      search: '',
      preview: null,
      previewJingleId: null,
      // Upload form
      showUploadForm: false,
      uploadTitle: '',
      selectedFile: null,
      uploading: false,
      // Delete
      jingleToDelete: null,
      deleting: false,
      // Order
      showOrderForm: false,
      orderText: '',
      ordering: false
    }
  },

  filters: {
    truncate (value, length) {
      if (!value) return ''
      if (value.length <= length) return value
      return value.substring(0, length) + '...'
    }
  },

  computed: {
    isPlaying () {
      return this.$store.state.audio.audioPlaying
    },
    jinglesForDisplay () {
      var search = this.search.trim().toLowerCase()
      return (
        search !== ''
          ? this.jingles.filter(jingle => jingle.titleLc.indexOf(search) !== -1)
          : this.jingles
      ).slice(0, this.displayLimit)
    },
    canUpload () {
      return this.selectedFile !== null && this.uploadTitle.trim() !== ''
    }
  },

  methods: {
    scrollHandler: scrollHandler('jingles'),
    previewClick (jingle) {
      if (this.isPlaying || jingle.preview_url == null || jingle.preview_url === '') { return }
      if (this.previewJingleId != null) {
        if (this.previewJingleId === jingle.id) {
          this.preview.pause()
          this.preview = null
          this.previewJingleId = null
        }
        return
      }
      this.preview = new Audio(jingle.preview_url)
      this.previewJingleId = jingle.id
      this.preview.onended = () => {
        this.preview = null
        this.previewJingleId = null
      }
      this.preview.play()
    },

    onFileSelected (event) {
      this.selectedFile = event.target.files[0] || null
    },

    async uploadJingle () {
      if (!this.canUpload) return
      this.uploading = true
      try {
        const response = await musicboxClient.uploadJingle(this.selectedFile, this.uploadTitle.trim())
        const newJingle = response.data.jingle_created
        newJingle.minutesAndSeconds = secondsToMinAndSec(newJingle.duration)
        newJingle.titleLc = newJingle.title.toLowerCase()
        this.jingles.unshift(newJingle)
        this.$notify.success(this.$t('Jingle uploaded'))
        // Reset form
        this.uploadTitle = ''
        this.selectedFile = null
        this.$refs.fileInput.value = ''
        this.showUploadForm = false
      } catch (error) {
        var msg = this.$t('Failed to upload jingle')
        if (error.response && error.response.data && error.response.data.detail) {
          msg = Array.isArray(error.response.data.detail) ? error.response.data.detail[0] : error.response.data.detail
        }
        this.$notify.danger(msg)
      } finally {
        this.uploading = false
      }
    },

    confirmDeleteJingle (jingle) {
      this.jingleToDelete = jingle
    },

    async deleteJingle () {
      if (!this.jingleToDelete) return
      this.deleting = true
      try {
        await musicboxClient.deleteJingle(this.jingleToDelete.id)
        this.jingles = this.jingles.filter(j => j.id !== this.jingleToDelete.id)
        this.$notify.success(this.$t('Jingle deleted'))
        this.jingleToDelete = null
      } catch (error) {
        var msg = this.$t('Failed to delete jingle')
        if (error.response && error.response.data && error.response.data.detail) {
          msg = Array.isArray(error.response.data.detail) ? error.response.data.detail[0] : error.response.data.detail
        }
        this.$notify.danger(msg)
      } finally {
        this.deleting = false
      }
    },

    async orderJingle () {
      if (!this.orderText.trim()) return
      this.ordering = true
      try {
        await musicboxClient.orderJingle(this.orderText.trim())
        this.$notify.success(this.$t('Jingle ordered'))
        this.orderText = ''
        this.showOrderForm = false
      } catch (error) {
        var msg = this.$t('Failed to order jingle')
        if (error.response && error.response.data && error.response.data.detail) {
          msg = error.response.data.detail
        }
        this.$notify.danger(msg)
      } finally {
        this.ordering = false
      }
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/Jingles.vue